/**
 * 联系方式 7 天自动轮换 —— 纯逻辑模块（前后端共用）
 *
 * ⚠️ 重要：本文件会同时被两处代码引用——
 *   1) 前端 utils/contactRotation.ts（通过 React Hook 渲染到页面）
 *   2) Cloudflare Pages Function functions/api/quote.ts（决定 Resend 抄送对象）
 * 修改本文件会同时影响「网站展示」和「quote 邮件抄送对象」，请谨慎调整。
 *
 * 业务规则：
 * - 以 BASE_DATE（2026-04-20 00:00 GMT+8）作为基准日。
 * - 从基准日起每满 7 天自然日，自动切换到数组中的下一个联系方式，循环复用。
 * - 例：2026-04-20 ~ 04-26 使用 Index 0；2026-04-27 ~ 05-03 使用 Index 1，以此类推。
 *
 * 纯函数 + 无副作用 + 不依赖任何框架/DOM/Node 专有 API，
 * 可在浏览器、Cloudflare Workers、Node.js 任意环境运行。
 */

export const CONTACT_EMAILS = [
  'info@jinghui-tech.com',
  'jhcool@jhcool.com',
  's2@jh-cool.com',
  's3@jinghui-tech.com',
] as const;

export const CONTACT_PHONES = [
  '+86-13774509657',
  '+86-15985755675',
  '+86-18606938031',
  '+86-15080458735',
] as const;

/**
 * 基准日（公司时区 GMT+8）。当天起的 7 天内显示 Index 0。
 * 使用 UTC 时间 2026-04-19T16:00:00Z == 2026-04-20T00:00:00+08:00，
 * 以避免访客浏览器本地时区 / Worker 所在数据中心时区差异导致的提前/延迟切换。
 */
export const BASE_DATE_UTC = Date.UTC(2026, 3, 19, 16, 0, 0); // 月份从 0 开始：3 = 4 月

export const ROTATION_DAYS = 7;
const MS_PER_DAY = 24 * 60 * 60 * 1000;
export const ROTATION_MS = ROTATION_DAYS * MS_PER_DAY;

/**
 * 计算给定时刻相对于基准日已经经过的「7 天周期」数量。
 * 基准日之前一律视为第 0 个周期（不会出现负数索引）。
 */
export function getRotationPeriod(now: Date = new Date()): number {
  const diff = now.getTime() - BASE_DATE_UTC;
  if (diff < 0) return 0;
  return Math.floor(diff / ROTATION_MS);
}

/**
 * 在指定列表中按 7 天周期取当前值。
 */
function pickByRotation<T>(list: readonly T[], now: Date = new Date()): T {
  if (list.length === 0) {
    throw new Error('Contact rotation list must not be empty');
  }
  const period = getRotationPeriod(now);
  return list[period % list.length];
}

export function getCurrentEmail(now: Date = new Date()): string {
  return pickByRotation(CONTACT_EMAILS, now);
}

export function getCurrentPhone(now: Date = new Date()): string {
  return pickByRotation(CONTACT_PHONES, now);
}

/**
 * 距离下一次轮换切换还有多少毫秒（用于前端安排自动刷新）。
 */
export function msUntilNextRotation(now: Date = new Date()): number {
  const diff = now.getTime() - BASE_DATE_UTC;
  if (diff < 0) return -diff; // 基准日之前：到基准日再切
  const remainder = diff % ROTATION_MS;
  return ROTATION_MS - remainder;
}
